import React, { useState, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import clsx from 'clsx';

// SVG icons for the component
const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const styles = {
  button: [
    'relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6',
    'dark:bg-zinc-900 dark:ring-gray-700 dark:text-white dark:focus:ring-blue-500',
  ],
  options: [
    'absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10',
    'dark:bg-zinc-800 dark:ring-gray-700',
  ],
  option: [
    'relative cursor-default select-none py-2 pl-10 pr-4',
    'ui-active:bg-blue-600 ui-active:text-white ui-not-active:text-gray-900',
    'dark:ui-not-active:text-white',
  ],
  icon: 'absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none',
  checkIcon: 'absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400',
  disabled: 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-zinc-800',
  sizes: {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  },
  error: [
    'ring-red-500 focus:ring-red-600 text-red-900',
    'dark:text-red-400',
  ],
};

export const Select = ({
  options = [],
  value = null,
  onChange,
  label = '',
  placeholder = 'Select an option',
  className = '',
  disabled = false,
  error = false,
  helperText = '',
  size = 'md',
  valueKey = 'value',
  labelKey = 'label',
  fullWidth = true,
  ...props
}) => {
  // Find the selected option object
  const selectedOption = options.find(option => 
    typeof option === 'object' ? option[valueKey] === value : option === value
  );
  
  // Create a function to get label from option
  const getOptionLabel = (option) => {
    if (option === null) return placeholder;
    return typeof option === 'object' ? option[labelKey] : option;
  };
  
  // Create a function to get value from option
  const getOptionValue = (option) => {
    return typeof option === 'object' ? option[valueKey] : option;
  };

  const handleChange = (option) => {
    onChange(getOptionValue(option));
  };

  return (
    <div className={clsx('relative', fullWidth ? 'w-full' : 'w-auto')}>
      {label && (
        <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-gray-200">
          {label}
        </label>
      )}

      <Listbox value={selectedOption || null} onChange={handleChange} disabled={disabled}>
        <div className="relative mt-1">
          <Listbox.Button 
            className={clsx(
              styles.button, 
              styles.sizes[size],
              disabled && styles.disabled,
              error && styles.error,
              className
            )}
          >
            <span className={clsx('block truncate', !selectedOption && 'text-gray-500')}>
              {selectedOption ? getOptionLabel(selectedOption) : placeholder}
            </span>
            <span className={styles.icon}>
              <ChevronDownIcon />
            </span>
          </Listbox.Button>
          
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className={styles.options}>
              {options.map((option, index) => (
                <Listbox.Option
                  key={index}
                  className={({ active }) => styles.option}
                  value={option}
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={clsx(
                          'block truncate',
                          selected ? 'font-medium' : 'font-normal'
                        )}
                      >
                        {getOptionLabel(option)}
                      </span>
                      {selected && (
                        <span className={styles.checkIcon}>
                          <CheckIcon />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>

      {(error || helperText) && (
        <p 
          className={clsx(
            'mt-1 text-sm',
            error ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'
          )}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
};
